import { useCallback } from 'react';
import type { TFunction } from 'i18next';
import { ApiError, NetworkError } from '../api/client';
import { useTranslation } from './index';
import { translateMessage } from './message';

/**
 * Текст любого отказа для показа человеку.
 *
 * Одна точка сборки на всё приложение: до фазы 10 каждый экран показывал
 * `error.message`, то есть ровно ту фразу, которую написал сервер. Теперь
 * фразы у сервера нет — есть ключ и код, — и собирать текст должен кто-то
 * один, иначе тридцать экранов соберут его тридцатью способами.
 *
 * Порядок разбора идёт от точного к общему:
 *
 *  1. Ключ сообщения — самый точный текст: сервер сказал, что именно
 *     не вышло, и приложил данные для подстановки.
 *  2. Код ошибки — общий текст причины: `AGENT_OFFLINE`, `NODE_NOT_FOUND`.
 *     Годится, когда ключ незнаком (сервер новее клиента).
 *  3. Общая фраза по виду отказа: сеть, отказ сервера, всё остальное.
 */
export function describeError(error: unknown, t: TFunction): string {
  if (error instanceof NetworkError) {
    return t('errors.network.unreachable');
  }

  if (error instanceof ApiError) {
    const byKey = translateMessage(error.message, t);
    if (byKey !== null) {
      return byKey;
    }

    const byCode = t(`errors.code.${error.code}`, { defaultValue: '' });
    if (byCode !== '') {
      return byCode;
    }

    /*
      Ни ключа, ни кода — но статус есть всегда, и по нему видно хотя бы,
      чья это беда. Сообщение сервера сюда не подставляется: с фазы 10 оно
      написано для журнала, а не для человека.
    */
    return t('errors.code.byStatus', { status: error.status });
  }

  if (error instanceof Error) {
    const byKey = translateMessage(error.message, t);
    return byKey ?? error.message;
  }

  return t('errors.unknown');
}

/**
 * Подробности проверки формы, если сервер их прислал.
 *
 * Каждая запись — «поле: что с ним не так». Показываются отдельной строкой
 * под общим текстом: список полей в одной фразе с общим объяснением
 * не читается, а без него человек не знает, куда смотреть.
 */
export function describeIssues(error: unknown, t: TFunction): string[] {
  if (!(error instanceof ApiError)) {
    return [];
  }

  const issues = error.details?.['issues'];

  if (!Array.isArray(issues)) {
    return [];
  }

  return issues
    .map((issue): string | null => {
      if (issue === null || typeof issue !== 'object') {
        return null;
      }

      const { path, message } = issue as { path?: unknown; message?: unknown };

      if (typeof message !== 'string') {
        return null;
      }

      const text = translateMessage(message, t) ?? message;

      return typeof path === 'string' && path.length > 0 ? `${path}: ${text}` : text;
    })
    .filter((line): line is string => line !== null);
}

/** Тот же разбор в виде хука — чтобы не тянуть `t` через все компоненты. */
export function useErrorText(): (error: unknown) => string {
  const { t } = useTranslation();

  return useCallback((error: unknown) => describeError(error, t), [t]);
}
